import { useState, type ReactNode } from 'react';
import { Trash2 } from '../../../icons';
import { Button, FormField, Input, Modal, ModalActions, Segmented } from '../../../components/shared';
import {
  useDashboardStore, BOARD_KIND_LABEL, BOARD_VISIBILITY_LABEL, BOARD_COLORS,
  type BoardColor, type BoardKind, type BoardVisibility, type DashboardBoard, type WidgetSpan,
} from '../../../store/dashboardStore';
import { getWidgetDef } from './widget-registry';
import './dashboard-frame-v2.css';

/**
 * 대시보드 설정 창 v2 (2026-09-16 · 대시보드 시안 v2 V5) — 깃발이 켜졌을 때만. 꺼져 있으면 `BoardSettingsModal` 그대로.
 *
 * 색 · 이름 · 유형 · 공개 범위 · 시작 화면 · 위젯 목록 · 삭제 칸.
 *
 * ★★★ 공개 범위(규칙 D11 · Q-4) — 「부서 공유」 · 「지정 멤버 공유」는 **보이되 새로 고를 수 없다**(준비 중).
 *     이미 그 값으로 저장된 대시보드는 **값을 그대로 둔다** — 창을 열고 저장만 눌렀는데 나만 보기로 바뀌면 안 된다.
 * ★★ 전사 유형은 「담당자만」이다 — 전사 대시보드를 열었으면 유형은 읽기만 한다(종전 창은 개인으로 바꿔 저장했다).
 * ★ 삭제는 칸으로 떼어 «함께 사라지는 것»을 먼저 적는다. 확인 창(`BoardDeleteModal`)은 종전 그대로 거친다.
 */

const SPAN_LABEL: Record<WidgetSpan, string> = { 4: '1/3', 6: '1/2', 8: '2/3', 12: '전체' };
/** 위젯 목록은 넷까지 적고 나머지는 「… n개 더」. 순서 · 폭은 레이아웃 편집에서 바꾼다. */
const LIST_MAX = 4;
const SHARE_SOON = '공유 대상 고르기는 아직 준비 중입니다';

function tagged(label: string, tag: string): ReactNode {
  return <>{label}{' '}<em className="dwv-soon">{tag}</em></>;
}

/** 유형 · 공개 범위 선택지 — 새 대시보드 창 v2 도 같은 것을 쓴다(두 창의 규칙이 갈라지지 않게). */
export const KIND_OPTIONS = [
  { value: 'personal', label: BOARD_KIND_LABEL.personal },
  { value: 'team', label: BOARD_KIND_LABEL.team },
  { value: 'project', label: BOARD_KIND_LABEL.project },
  { value: 'corp', label: tagged(BOARD_KIND_LABEL.corp, '담당자만'), disabled: true, disabledReason: '전사 대시보드는 회사 담당자만 정합니다' },
];

export const VISIBILITY_OPTIONS = [
  { value: 'private', label: BOARD_VISIBILITY_LABEL.private },
  { value: 'team', label: tagged(BOARD_VISIBILITY_LABEL.team, '준비 중'), disabled: true, disabledReason: SHARE_SOON },
  { value: 'members', label: tagged(BOARD_VISIBILITY_LABEL.members, '준비 중'), disabled: true, disabledReason: SHARE_SOON },
];

const LANDING_OPTIONS = [
  { value: 'this', label: '로그인하면 이 대시보드로' },
  { value: 'last', label: '마지막에 본 것으로' },
];

export function BoardSettingsV2Modal({
  board, onClose, onDelete,
}: {
  board: DashboardBoard;
  onClose: () => void;
  /** 삭제 칸의 단추 — 삭제 확인 창으로 넘긴다. */
  onDelete?: (board: DashboardBoard) => void;
}) {
  const updateBoard = useDashboardStore((s) => s.updateBoard);
  const setDefaultBoard = useDashboardStore((s) => s.setDefaultBoard);
  const defaultBoardId = useDashboardStore((s) => s.defaultBoardId);

  const [name, setName] = useState(board.name);
  const [kind, setKind] = useState<BoardKind>(board.kind);
  const [visibility, setVisibility] = useState<BoardVisibility>(board.visibility);
  const [color, setColor] = useState<BoardColor | undefined>(board.color);
  const [landing, setLanding] = useState<'this' | 'last'>(defaultBoardId === board.id ? 'this' : 'last');

  const corp = board.kind === 'corp';
  const count = board.widgets.length;
  const rest = count - LIST_MAX;

  const visibilityHint = board.visibility === 'private'
    ? '나만 봅니다 — 탭도 나에게만 있습니다.'
    : `지금 값(${BOARD_VISIBILITY_LABEL[board.visibility]})을 그대로 둡니다 · 나만 보기로 바꾸면 다시 고를 수 없습니다(준비 중)`;

  const save = () => {
    updateBoard(board.id, {
      name: name.trim() || board.name,
      ...(corp ? {} : { kind }),
      ...(board.locked ? {} : { visibility }),
      ...(color ? { color } : {}),
    });
    // 시작 화면 — 이 대시보드로 정했으면 기본으로, 풀었는데 이 대시보드였으면 «마지막에 본 것»으로.
    if (landing === 'this') setDefaultBoard(board.id);
    else if (defaultBoardId === board.id) setDefaultBoard(null);
    onClose();
  };

  return (
    <Modal
      title="대시보드 설정" onClose={onClose} size="md"
      footer={(
        <ModalActions>
          <Button variant="secondary" onClick={onClose}>취소</Button>
          <Button variant="primary" onClick={save}>저장</Button>
        </ModalActions>
      )}
    >
      {/*
        확인 요청 (2026-09-16 · 육안 확인) — 잠긴 전사 대시보드도 이 창이 열린다. 이름 · 색은 store 가 잠긴 판을
        고치지 않아(`patchBoard`) 저장해도 **조용히 무시**됐다 → 막고 까닭을 적는다. 시작 화면은 «나의» 설정이라 그대로 고른다.
      */}
      <FormField label="색" hint={board.locked ? '회사가 정한 대시보드라 색 · 이름은 바꿀 수 없습니다.' : '탭의 점이 이 색이 됩니다.'}>
        <span className="dwc-swatches" role="radiogroup" aria-label="대시보드 색" aria-disabled={board.locked || undefined}>
          {BOARD_COLORS.map((c) => (
            <button
              key={c.value}
              type="button" role="radio" aria-checked={color === c.value} aria-label={c.label}
              className={`dwc-swatch${color === c.value ? ' is-on' : ''}`}
              style={{ ['--c' as string]: c.token }}
              disabled={board.locked}
              onClick={() => setColor(c.value)}
            />
          ))}
        </span>
      </FormField>

      <FormField label="이름" required hint={board.locked ? '회사 담당자가 정한 이름입니다 — 시작 화면만 고를 수 있습니다.' : '탭에 이 이름이 보입니다.'}>
        <Input value={name} maxLength={40} fullWidth disabled={board.locked} onChange={(e) => setName(e.target.value)} />
      </FormField>

      <FormField label="유형">
        <Segmented
          size="sm" ariaLabel="유형" value={kind} options={KIND_OPTIONS} disabled={corp}
          onChange={(v) => setKind(v as BoardKind)}
          hint={corp ? '전사 대시보드는 회사 담당자가 관리합니다.' : undefined}
        />
      </FormField>

      <FormField label="공개 범위">
        <Segmented
          size="sm" ariaLabel="공개 범위" value={visibility} options={VISIBILITY_OPTIONS} disabled={board.locked}
          onChange={(v) => setVisibility(v as BoardVisibility)}
          hint={visibilityHint}
        />
      </FormField>

      <FormField label="시작 화면">
        <Segmented
          size="sm" ariaLabel="시작 화면" value={landing} options={LANDING_OPTIONS}
          onChange={(v) => setLanding(v as 'this' | 'last')}
          hint="로그인하면 어느 대시보드부터 볼지 정합니다."
        />
      </FormField>

      <FormField label={`위젯 ${count}개`} hint="순서와 폭은 「레이아웃 편집」에서 바꿉니다.">
        {count === 0
          ? <p className="dwc-empty">아직 위젯이 없습니다.</p>
          : (
            <ul className="dwv-wlist" aria-label="이 대시보드의 위젯">
              {board.widgets.slice(0, LIST_MAX).map((w) => (
                <li key={w.id}>
                  <span>{getWidgetDef(w.widgetKey)?.label ?? w.widgetKey}</span>
                  <em>{SPAN_LABEL[w.span]}</em>
                </li>
              ))}
              {rest > 0 && <li className="dwv-wlist__more">… {rest}개 더</li>}
            </ul>
          )}
      </FormField>

      {onDelete && !board.locked && (
        <div className="dwv-dzone">
          <div className="dwv-dzone__t">
            <b>대시보드 삭제</b>
            <span>{count > 0 ? `배치한 위젯 ${count}개가 함께 사라집니다 · ` : ''}되돌릴 수 없습니다</span>
          </div>
          <Button
            variant="danger" size="sm" iconLeft={<Trash2 size={14} />}
            onClick={() => { onClose(); onDelete(board); }}
          >
            대시보드 삭제
          </Button>
        </div>
      )}
    </Modal>
  );
}
